import { useEffect, useRef, useState } from 'react';

/**
 * Lazy-loaded FB SDK hook (P4·S6).
 *
 * - Injects `connect.facebook.net/en_US/sdk.js` once per page.
 * - Calls `FB.init` with the supplied `appId` only after the SDK
 *   reports ready.
 * - Exposes `login(config)` which wraps `FB.login` with the
 *   embedded-signup `config_id` + `extras: { feature: 'whatsapp_embedded_signup' }`,
 *   resolving with the OAuth `code` on success or rejecting with a
 *   structured error on user-cancel / SDK error.
 *
 * The hook is one-call-per-mount safe: `login()` always uses the
 * latest `appId`/`configId`, the SDK script tag is keyed off a
 * module-level boolean so multiple components mounting it never
 * inject duplicates.
 */

type FbScope = string;

/**
 * `FB.login` delivers the result as `{ status, authResponse }`. For
 * embedded signup with `response_type: 'code'`, the OAuth code lives at
 * `authResponse.code` — NOT at the top level. Reading `resp.code` (as the
 * old code did) is always undefined, so every login was treated as a failure.
 */
interface FbAuthResponse {
  status?: 'connected' | 'not_authorized' | 'unknown';
  authResponse?: {
    code?: string;
    accessToken?: string;
    userID?: string;
  } | null;
  error?: { message?: string };
}

interface FbStatic {
  init: (opts: { appId: string; cookie?: boolean; xfbml?: boolean; version: string }) => void;
  login: (
    cb: (resp: FbAuthResponse) => void,
    options: {
      scope: FbScope;
      config_id?: string;
      response_type?: 'code';
      override_default_response_type?: boolean;
      auth_type?: 'rerequest';
      return_scopes?: boolean;
      extras?: Record<string, unknown>;
    },
  ) => void;
}

/**
 * Meta's embedded-signup flow also posts the selected WABA + phone number
 * IDs via `window.postMessage` (origin facebook.com) as a side channel —
 * these are more reliable than re-deriving the WABA from the access token.
 */
interface WaEmbeddedSignupSessionInfo {
  waba_id?: string;
  phone_number_id?: string;
}

declare global {
  interface Window {
    FB?: FbStatic;
    fbAsyncInit?: () => void;
  }
}

let scriptInjected = false;

export interface FbLoginConfig {
  app_id: string;
  config_id: string;
  scope: string;
}

export interface FbLoginResult {
  code: string;
  waba_id?: string;
  phone_number_id?: string;
}

const FB_SDK_VERSION = 'v21.0';

export function useFbSdk(appId: string | null): {
  isReady: boolean;
  login: (config: FbLoginConfig) => Promise<FbLoginResult>;
  loginForToken: (scope: string) => Promise<string>;
} {
  const [isReady, setIsReady] = useState(false);
  const initialisedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!appId) return;
    // FB.init is idempotent enough that re-init with the same appId
    // is fine — but skip it when we've already done it.
    if (initialisedFor.current === appId && window.FB) {
      setIsReady(true);
      return;
    }

    const onReady = (): void => {
      window.FB?.init({
        appId,
        cookie: true,
        xfbml: false,
        version: FB_SDK_VERSION,
      });
      initialisedFor.current = appId;
      setIsReady(true);
    };

    if (window.FB) {
      onReady();
      return;
    }

    if (!scriptInjected) {
      scriptInjected = true;
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    // The SDK calls window.fbAsyncInit when loaded.
    window.fbAsyncInit = onReady;
  }, [appId]);

  const login = (config: FbLoginConfig): Promise<FbLoginResult> => {
    return new Promise((resolve, reject) => {
      if (!window.FB) {
        reject(new Error('FB SDK not loaded'));
        return;
      }

      // Capture the WABA/phone IDs that embedded signup posts back via
      // window.postMessage while the popup is open. We resolve with these
      // alongside the OAuth code so the backend can use them directly.
      let sessionInfo: WaEmbeddedSignupSessionInfo = {};
      const onMessage = (event: MessageEvent): void => {
        // Opaque origins ("", "null" — sandboxed iframes/extensions) make
        // `new URL()` throw, so guard it. Anchor the host check so only
        // facebook.com (and subdomains) match, not evil-facebook.com.
        let host: string;
        try {
          host = new URL(event.origin).hostname;
        } catch {
          return;
        }
        if (host !== 'facebook.com' && !host.endsWith('.facebook.com')) return;
        try {
          const parsed =
            typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (parsed?.type === 'WA_EMBEDDED_SIGNUP' && parsed?.data) {
            sessionInfo = {
              waba_id: parsed.data.waba_id,
              phone_number_id: parsed.data.phone_number_id,
            };
          }
        } catch {
          // Non-JSON messages on the channel — ignore.
        }
      };
      window.addEventListener('message', onMessage);
      const cleanup = (): void => window.removeEventListener('message', onMessage);

      window.FB.login(
        (resp) => {
          const code = resp?.authResponse?.code;
          if (code) {
            cleanup();
            resolve({ code, ...sessionInfo });
            return;
          }
          cleanup();
          if (resp?.status === 'not_authorized') {
            reject(
              Object.assign(new Error('User denied permission'), {
                code: 'USER_CANCEL',
              }),
            );
            return;
          }
          reject(
            Object.assign(
              new Error(resp?.error?.message ?? 'Facebook login failed'),
              { code: 'FB_LOGIN_FAILED' },
            ),
          );
        },
        {
          scope: config.scope,
          config_id: config.config_id,
          response_type: 'code',
          override_default_response_type: true,
          extras: { feature: 'whatsapp_embedded_signup', sessionInfoVersion: 2 },
        },
      );
    });
  };

  /**
   * Plain Facebook Login (no embedded signup) — resolves with the short-lived
   * user access token. `rerequest` re-asks for a permission the person
   * switched off last time.
   */
  const loginForToken = (scope: string): Promise<string> =>
    new Promise((resolve, reject) => {
      if (!window.FB) {
        reject(new Error('FB SDK not loaded'));
        return;
      }
      window.FB.login(
        (resp) => {
          const token = resp?.authResponse?.accessToken;
          if (token) {
            resolve(token);
            return;
          }
          // No token and no error = the person closed the popup or said no
          // (status 'unknown' / 'not_authorized'): not a failure to report.
          reject(
            Object.assign(new Error(resp?.error?.message ?? 'Facebook login was cancelled'), {
              code: resp?.error ? 'FB_LOGIN_FAILED' : 'USER_CANCEL',
            }),
          );
        },
        { scope, auth_type: 'rerequest', return_scopes: true },
      );
    });

  return { isReady, login, loginForToken };
}
